import { useEffect, useState } from 'react';

/**
 * Subscribe to a CSS media query from JavaScript.
 *
 * Reach for this only when a breakpoint changes *behaviour*, not appearance — Tailwind's
 * `xl:` variants stay the right tool for layout. The assistant panel is the case that needs
 * it (docs/24 P4/P5): below 1280px the top-bar control navigates to `/ask` instead of
 * docking a panel, and the left nav drops to its rail only while a panel is docked. Both are
 * decisions, not styles, and a CSS-only version would mean two controls in the DOM with one
 * of them `display:none` — present to a screen reader's cursor in every mode where it should
 * not be.
 *
 * jsdom has no `matchMedia`, so the guards are load-bearing for tests, not defensive noise.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && window.matchMedia(query).matches,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(query);
    // Re-read on subscribe: the width can change between the initial render and this effect.
    setMatches(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** Tailwind's `xl:` breakpoint — the width at which a docked panel and the content column
 *  can share the screen (docs/24 P4 does the arithmetic). */
export const XL_QUERY = '(min-width: 1280px)';
